import { useRef, useState, useCallback, useEffect } from "react"
import { checkVideo } from "../utils/isVideo"
import FullScreenLoader from "../components/utils/FullScreenLoader"
import { MySelector, MyDispatch } from "../redux/store"
import FullScreenNav from "../components/navs/FullScreenNav"
import MetaTags from "../components/utils/MetaTags"
import { useDispatch } from "react-redux"
import { setFullScreenObject } from "../redux/utilSlice"
import WatermarkOverlay from "../components/utils/WatermarkOverlay"
import { getOptimalUrl, getOptimalUrls } from "../utils/getOptimalUrl"

interface Photos {
  id: string
  name: string
  url: string
  folderId: string
  compressedUrl?: string
  b2Url?: string
  b2CompressedUrl?: string
}

const MediaContent = ({ item, eventData }: { item: Photos; eventData?: any }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const { url: optimalUrl, compressedUrl: optimalCompressedUrl } = getOptimalUrls(item)
  const hasCompressed = optimalCompressedUrl && optimalCompressedUrl !== optimalUrl

  // Reset image loaded state when item changes
  useEffect(() => {
    setImageLoaded(false)
  }, [item.id])

  if (checkVideo(optimalUrl)) {
    return (
      <video
        ref={videoRef}
        key={item.id}
        className="h-full object-contain"
        controls
        autoPlay
        playsInline
        muted
        preload="auto"
      >
        <source src={optimalCompressedUrl} type="video/mp4" />
      </video>
    )
  }

  return (
    <div className="relative h-full">
      {/* Compressed image as placeholder */}
      {hasCompressed && !imageLoaded && (
        <img
          src={optimalCompressedUrl}
          alt={item.name}
          loading="eager"
          className="h-full object-contain"
        />
      )}

      {/* Original high-res image */}
      <img
        src={optimalUrl}
        alt={item.name}
        loading="eager"
        className={`h-full object-contain transition-opacity duration-300 ${
          hasCompressed ? (imageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0') : ''
        }`}
        onLoad={() => setImageLoaded(true)}
      />

      {/* Watermark Overlay */}
      {eventData?.watermarkEnabled && eventData?.watermarkText && (
        <WatermarkOverlay
          watermarkText={eventData.watermarkText}
          className="h-full"
        />
      )}
    </div>
  )
}

const FullScreen = () => {
  const dispatch = useDispatch<MyDispatch>()
  const { fullScreenObject, currentFolderData, type } = MySelector((state) => state.util)
  const { activeAlbumFiles } = MySelector((state) => state.gallery)
  const { category, data: eventData } = MySelector((state) => state.event)

  const [isSlideshow, setIsSlideshow] = useState(false)
  const slideshowInterval = useRef<NodeJS.Timeout | null>(null)
  const slideFilesRef = useRef<Array<Photos>>()
  const fullScreenObjectRef = useRef<Photos | null>(null)

  // Set up slide files and current object ref
  useEffect(() => {
    if (!fullScreenObject || !currentFolderData) return
    if (type === "physical" && !activeAlbumFiles) return

    const files = type === "gallery" ? currentFolderData : activeAlbumFiles || []
    slideFilesRef.current = files
    fullScreenObjectRef.current = fullScreenObject
  }, [fullScreenObject, currentFolderData, activeAlbumFiles, type, category])

  // Update fullScreenObject ref whenever it changes
  useEffect(() => {
    fullScreenObjectRef.current = fullScreenObject || null
  }, [fullScreenObject])


  // Slideshow controls
  const toggleSlideshow = useCallback(() => {
    setIsSlideshow(prev => !prev)
  }, [])

  // manage slideshow interval
  useEffect(() => {
    if (isSlideshow) {
      slideshowInterval.current = setInterval(() => {
        const files = slideFilesRef.current
        const currentObj = fullScreenObjectRef.current
        if (!files || !currentObj) return

        const currentIndex = files.findIndex((sf) => sf.id === currentObj.id)

        // Find next non-video file
        let newIndex = currentIndex + 1
        while (newIndex < files.length && checkVideo(getOptimalUrl(files[newIndex], false))) {
          newIndex++
        }

        if (newIndex >= files.length) {
          // Stop slideshow when reaching the end
          setIsSlideshow(false)
          return
        }

        const newSlide = files[newIndex]
        dispatch(setFullScreenObject(newSlide))
      }, 3000)
    } else {
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current)
        slideshowInterval.current = null
      }
    }

    return () => {
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current)
        slideshowInterval.current = null
      }
    }
  }, [isSlideshow, dispatch])

  if (!fullScreenObject) return <FullScreenLoader />

  return (
    <div className="fixed bg-black h-screen w-screen z-[5151] flex flex-col">
      <MetaTags fileId={fullScreenObject.id} fileUrl={getOptimalUrl(fullScreenObject, false)} />
      <FullScreenNav isSlideshow={isSlideshow} onToggleSlideshow={toggleSlideshow} />


      <div className="flex flex-1 justify-center items-center overflow-hidden -translate-y-[28px] md:-translate-y-0">
        <MediaContent item={fullScreenObject} eventData={eventData} />
      </div>
      {/* Fixed height block for mobile browser UI */}
      <div className="h-16 md:h-0 shrink-0" />
    </div>
  )
}

export default FullScreen
